import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import {
  clearValidationField,
  getApiError,
  getImageValidationMessages,
  getOrderFromResponse,
  getResponseStatus,
  INITIAL_VALUES,
  normalizeValidationError,
  validateDisplayName,
} from './CustomerReviewCreate.helpers';
import useCustomerReviewPhotos from './useCustomerReviewPhotos';

const LOOKUP_DELAY_MS = 500;

const useCustomerReviewCreate = ({ createReview, lookupOrder, uploadImage }) => {
  const [values, setValues] = useState(INITIAL_VALUES);
  const [order, setOrder] = useState(null);
  const [lookupState, setLookupState] = useState('idle');
  const [displayNameError, setDisplayNameError] = useState('');
  const [displayNameTouched, setDisplayNameTouched] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);
  const photoState = useCustomerReviewPhotos({
    invoiceNumber: values.invoiceNumber,
    uploadImage,
  });

  const invoiceRef = useRef('');
  const inFlightInvoiceRef = useRef(null);
  const isMountedRef = useRef(true);
  const lookupRequestIdRef = useRef(0);
  const settledInvoiceRef = useRef(null);

  useEffect(() => () => {
    isMountedRef.current = false;
  }, []);

  const runLookup = useCallback(async invoiceNumber => {
    if (!invoiceNumber) return;

    const requestId = lookupRequestIdRef.current + 1;
    lookupRequestIdRef.current = requestId;
    inFlightInvoiceRef.current = invoiceNumber;
    setOrder(null);
    setLookupState('checking');

    try {
      const response = await lookupOrder(invoiceNumber);
      if (
        !isMountedRef.current
        || lookupRequestIdRef.current !== requestId
        || invoiceRef.current !== invoiceNumber
      ) return;

      const resolvedOrder = getOrderFromResponse(response);
      const isEligibleOrder = Boolean(
        resolvedOrder
        && resolvedOrder.invoice_number === invoiceNumber
        && resolvedOrder.customer_received != null
      );

      settledInvoiceRef.current = invoiceNumber;
      setOrder(isEligibleOrder ? resolvedOrder : null);
      setLookupState(isEligibleOrder ? 'found' : 'notFound');
    } catch (error) {
      if (
        !isMountedRef.current
        || lookupRequestIdRef.current !== requestId
        || invoiceRef.current !== invoiceNumber
      ) return;

      if (getResponseStatus(error) !== 401) setLookupState('error');
    } finally {
      if (lookupRequestIdRef.current === requestId) {
        inFlightInvoiceRef.current = null;
      }
    }
  }, [lookupOrder]);

  const debouncedLookup = useMemo(
    () => debounce(runLookup, LOOKUP_DELAY_MS),
    [runLookup]
  );

  useEffect(() => () => {
    debouncedLookup.cancel();
  }, [debouncedLookup]);

  const clearServerField = useCallback(field => {
    setValidationError(currentError => clearValidationField(currentError, field));
  }, []);

  const handleInvoiceChange = event => {
    const invoiceNumber = event.target.value;
    const shouldExplainPhotoReset = Boolean(order && photoState.photos.length > 0);
    setAlert(null);
    photoState.clearPhotos(shouldExplainPhotoReset
      ? 'Photos were cleared because the invoice changed.'
      : '');
    invoiceRef.current = invoiceNumber;
    lookupRequestIdRef.current += 1;
    inFlightInvoiceRef.current = null;
    settledInvoiceRef.current = null;

    setValues(currentValues => ({
      ...currentValues,
      invoiceNumber,
    }));
    setOrder(null);
    setLookupState('idle');
    clearServerField('invoice_number');

    if (!invoiceNumber) {
      debouncedLookup.cancel();
      return;
    }

    debouncedLookup(invoiceNumber);
  };

  const triggerImmediateLookup = () => {
    const invoiceNumber = invoiceRef.current;
    debouncedLookup.cancel();
    if (
      !invoiceNumber
      || inFlightInvoiceRef.current === invoiceNumber
      || settledInvoiceRef.current === invoiceNumber
    ) return;
    runLookup(invoiceNumber);
  };

  const handleInvoiceKeyDown = event => {
    if (event.key === 'Enter') {
      event.preventDefault();
      triggerImmediateLookup();
    }
  };

  const handleDisplayNameChange = event => {
    const displayName = event.target.value;
    setAlert(null);
    setValues(currentValues => ({
      ...currentValues,
      displayName,
    }));
    clearServerField('display_name');
    if (displayNameTouched) setDisplayNameError(validateDisplayName(displayName));
  };

  const handleDisplayNameBlur = () => {
    const displayName = values.displayName.trim();
    setValues(currentValues => ({
      ...currentValues,
      displayName,
    }));
    setDisplayNameTouched(true);
    setDisplayNameError(validateDisplayName(displayName));
  };

  const handleRatingChange = rating => {
    setAlert(null);
    setValues(currentValues => ({
      ...currentValues,
      rating,
    }));
    clearServerField('rating');
  };

  const handleReviewTextChange = event => {
    const reviewText = event.target.value;
    setAlert(null);
    setValues(currentValues => ({
      ...currentValues,
      reviewText,
    }));
    clearServerField('review_text');
  };

  const resetForm = () => {
    debouncedLookup.cancel();
    lookupRequestIdRef.current += 1;
    invoiceRef.current = '';
    inFlightInvoiceRef.current = null;
    settledInvoiceRef.current = null;
    setValues(INITIAL_VALUES);
    setOrder(null);
    setLookupState('idle');
    setDisplayNameError('');
    setDisplayNameTouched(false);
    setValidationError(null);
    setAlert(null);
    photoState.clearPhotos('');
  };

  const handleSubmit = async event => {
    event.preventDefault();

    const displayName = values.displayName.trim();
    const localDisplayNameError = validateDisplayName(displayName);
    setDisplayNameTouched(true);
    setDisplayNameError(localDisplayNameError);
    setValues(currentValues => ({
      ...currentValues,
      displayName,
    }));

    if (
      !order
      || photoState.isUploading
      || localDisplayNameError
      || !displayName
      || !values.rating
    ) return;

    const payload = {
      invoice_number: values.invoiceNumber,
      display_name: displayName,
      rating: values.rating,
    };
    if (values.reviewText) payload.review_text = values.reviewText;
    if (photoState.imageUrls.length > 0) payload.images = photoState.imageUrls;

    setIsSubmitting(true);
    setAlert(null);
    setValidationError(null);
    photoState.clearPhotoAlert();

    try {
      await createReview(payload);
      if (!isMountedRef.current) return;

      resetForm();
      setAlert({ color: 'success', message: 'Review created' });
    } catch (error) {
      if (!isMountedRef.current) return;

      const status = getResponseStatus(error);
      if (status === 401) return;

      if (status === 422) {
        const apiError = getApiError(error);
        const imageValidationMessages = getImageValidationMessages(apiError);
        const normalizedError = normalizeValidationError(apiError);
        if (imageValidationMessages.length > 0) {
          photoState.setValidationMessages(imageValidationMessages);
        }
        if (normalizedError) {
          setValidationError(normalizedError);
        }
        if (normalizedError || imageValidationMessages.length > 0) return;
      }

      let message = 'Could not create this review, please try again.';
      let description = '';
      let invoiceInvalid = false;
      if (status === 404) {
        message = 'Order not found or not eligible for review';
        description = 'This order failed the eligibility check on submit. Your input and uploaded photos have been kept.';
        invoiceInvalid = true;
      } else if (status === 409) {
        message = 'This order already has a review';
        description = 'Only one review can exist per order. Your input and uploaded photos have been kept.';
        invoiceInvalid = true;
      }
      setAlert({ color: 'danger', description, invoiceInvalid, message });
    } finally {
      if (isMountedRef.current) setIsSubmitting(false);
    }
  };

  return {
    addPhotos: photoState.addPhotos,
    alert,
    clearAlert: () => setAlert(null),
    displayNameError,
    handleDisplayNameBlur,
    handleDisplayNameChange,
    handleInvoiceChange,
    handleInvoiceKeyDown,
    handleRatingChange,
    handleReviewTextChange,
    handleSubmit,
    isSubmitting,
    isUploadingPhotos: photoState.isUploading,
    lookupState,
    order,
    photoAlert: photoState.photoAlert,
    photos: photoState.photos,
    removePhoto: photoState.removePhoto,
    resetForm,
    retryPhoto: photoState.retryPhoto,
    triggerImmediateLookup,
    validationError,
    values,
  };
};

export default useCustomerReviewCreate;
